import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, ShieldAlert, Share } from 'lucide-react';
import { usePushEnrolment } from '../hooks/usePushEnrolment';

/**
 * "Turn on alerts", in the one place someone is already thinking about alerts.
 *
 * It lives in the notification drawer's header rather than on a settings screen because
 * until this shipped nobody had ever been asked, and a prompt filed behind a menu nobody
 * opens is the same as no prompt. The rest of the push surface — device list, health,
 * per-platform sound guidance — belongs on `/settings/notifications`, which this does
 * not build.
 *
 * Nothing here prompts on render. The permission dialog is fired by the tap and only by
 * the tap: browsers reject a prompt asked for on load, and being ambushed by a system
 * dialog is how someone learns to press Deny.
 */
export function PushEnableBanner() {
  const { t } = useTranslation();
  const { prompt, result, busy, enable } = usePushEnrolment();

  // Shown after a successful enrol, when `prompt` has already fallen back to 'hidden' —
  // otherwise the banner would simply vanish and the tap would have no visible outcome.
  if (result?.status === 'subscribed') {
    return (
      <Note
        tone="good"
        icon={<BellRing className="size-4" />}
        text={t('notifications.push.enabled')}
      />
    );
  }

  if (prompt === 'hidden') return null;
  if (prompt === 'unsupported') {
    return <Note tone="muted" text={t('notifications.push.unsupported')} />;
  }
  if (prompt === 'blocked') {
    return (
      <Note
        tone="warn"
        icon={<ShieldAlert className="size-4" />}
        text={t('notifications.push.blocked')}
      />
    );
  }
  if (prompt === 'install-first') return <InstallFirst />;

  return (
    <div className="border-t border-slate-800 bg-slate-900/60 px-4 py-3">
      <p className="text-sm font-semibold text-slate-100">{t('notifications.push.offerTitle')}</p>
      <p className="mt-1 text-xs text-slate-400">{t('notifications.push.offerBody')}</p>

      <button
        type="button"
        onClick={() => void enable()}
        disabled={busy}
        className="mt-3 min-h-[48px] w-full rounded-xl bg-brookfields-green px-4 text-sm font-semibold text-white ring-1 ring-brookfields-gold/70 disabled:opacity-60"
      >
        {busy ? t('notifications.push.enabling') : t('notifications.push.enable')}
      </button>

      {result && <Failure result={result} />}
    </div>
  );
}

/** The outcomes a refreshed `prompt` cannot express on its own. */
function Failure({
  result,
}: {
  result: NonNullable<ReturnType<typeof usePushEnrolment>['result']>;
}) {
  const { t } = useTranslation();

  if (result.status === 'no-vapid-key') {
    return <p className="mt-2 text-xs text-amber-400">{t('notifications.push.noKey')}</p>;
  }
  if (result.status === 'failed') {
    return (
      <p role="alert" className="mt-2 text-xs text-red-400">
        {t('notifications.push.failed', { error: result.error })}
      </p>
    );
  }
  // 'subscribed' is handled above; 'unsupported' and 'permission-denied' are already
  // reflected by the prompt this component re-read after the attempt.
  return null;
}

/**
 * iOS in a browser tab. Instructions, and deliberately no button.
 *
 * Safari does not expose `PushManager` at all until the app is on the Home Screen, so an
 * enable button here could only fail. Saying whose restriction this is matters: staff
 * otherwise conclude the app is broken.
 */
function InstallFirst() {
  const { t } = useTranslation();

  return (
    <div className="border-t border-slate-800 bg-slate-900/60 px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
        <Share className="size-4 shrink-0" aria-hidden="true" />
        {t('notifications.push.installTitle')}
      </p>
      <p className="mt-1 text-xs text-slate-400">{t('notifications.push.installBody')}</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-slate-300">
        <li>{t('notifications.push.installStep1')}</li>
        <li>{t('notifications.push.installStep2')}</li>
        <li>{t('notifications.push.installStep3')}</li>
      </ol>
    </div>
  );
}

const TONES = {
  good: 'text-emerald-400',
  warn: 'text-amber-400',
  muted: 'text-slate-500',
} as const;

function Note({ tone, icon, text }: { tone: keyof typeof TONES; icon?: ReactNode; text: string }) {
  return (
    <p
      className={`flex items-start gap-2 border-t border-slate-800 bg-slate-900/60 px-4 py-3 text-xs ${TONES[tone]}`}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{text}</span>
    </p>
  );
}
